import React, { useState, useEffect, useMemo, useRef } from "react";
import {
  collection,
  query,
  where,
  getDocs,
  doc,
  updateDoc,
  addDoc,
  arrayUnion,
  serverTimestamp,
} from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { db, storage } from "../../../firebase";
import {
  FaSearch,
  FaTimes,
  FaCircleNotch,
  FaEdit,
  FaHistory,
  FaMapMarkerAlt,
  FaWhatsapp,
  FaBoxOpen,
  FaGem,
  FaCopy,
  FaCamera,
  FaArrowRight,
  FaCheckCircle,
  FaExpandArrowsAlt,
  FaRedoAlt,
} from "react-icons/fa";
import "./RetailerOrders.scss";

// --- ENHANCED UX UTILITIES ---
const normalizeStatus = (s) =>
  s
    ? String(s).trim().charAt(0).toUpperCase() +
      String(s).trim().slice(1).toLowerCase()
    : "Pending";

// Advanced Deep-Scan Image Extractor (Safeguarded for Arrays/Strings)
const extractImages = (order) => {
  let imgs = [];
  if (order?.files && Array.isArray(order.files)) imgs.push(...order.files);
  if (order?.images && Array.isArray(order.images)) imgs.push(...order.images);

  if (order?.singleItems?.length > 0) {
    order.singleItems.forEach((item) => {
      if (item.fileUrls && Array.isArray(item.fileUrls))
        imgs.push(...item.fileUrls);
      else if (typeof item.fileUrls === "string") imgs.push(item.fileUrls);

      if (item.fileUrl && typeof item.fileUrl === "string")
        imgs.push(item.fileUrl);
    });
  }
  if (
    order?.castingDetails?.fileUrls &&
    Array.isArray(order.castingDetails.fileUrls)
  )
    imgs.push(...order.castingDetails.fileUrls);

  if (order?.items?.length > 0)
    order.items.forEach((i) => {
      if (i.fileUrls && Array.isArray(i.fileUrls)) imgs.push(...i.fileUrls);
    });

  if (
    order?.finalizationData?.images &&
    Array.isArray(order.finalizationData.images)
  )
    imgs.push(...order.finalizationData.images);

  // Remove duplicates and invalid links
  return [...new Set(imgs.filter((url) => url && typeof url === "string"))];
};

const formatDate = (timestamp) => {
  if (!timestamp) return "N/A";
  const date = timestamp.seconds
    ? new Date(timestamp.seconds * 1000)
    : new Date(timestamp);
  return date.toLocaleDateString("en-IN", {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const getProgressStep = (status) => {
  if (status === "Pending") return 1;
  if (status === "Processing") return 2;
  if (["Polishing", "Setting", "Hallmarked"].includes(status)) return 3;
  if (["Complete", "Delivered"].includes(status)) return 4;
  return 1;
};

const generateOrderId = async () => {
  const date = new Date();
  const monthPrefix = `SV${date.getFullYear().toString().slice(-2)}${(date.getMonth() + 1).toString().padStart(2, "0")}`;
  const currentDay = date.getDate().toString().padStart(2, "0");
  const snapshot = await getDocs(collection(db, "partyOrders"));
  let highestSequence = 0;
  snapshot.docs.forEach((doc) => {
    const id = doc.data().orderId;
    if (id && id.startsWith(monthPrefix)) {
      const seq = parseInt(id.substring(8), 10);
      if (!isNaN(seq) && seq > highestSequence) highestSequence = seq;
    }
  });
  return `${monthPrefix}${currentDay}${(highestSequence + 1).toString().padStart(3, "0")}`;
};

const RetailerOrders = () => {
  const [orders, setOrders] = useState([]);
  const [filteredOrders, setFilteredOrders] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  // Filters
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("All");

  // Selected Order States
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [isClosing, setIsClosing] = useState(false);
  const [lightboxImg, setLightboxImg] = useState(null);

  // NEW: Image Gallery State
  const [activeImageIndex, setActiveImageIndex] = useState(0);

  // Modes: 'view', 'edit', 'variation'
  const [drawerMode, setDrawerMode] = useState("view");
  const [isSaving, setIsSaving] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");

  // Forms
  const [editForm, setEditForm] = useState({});
  const [variationForm, setVariationForm] = useState({
    qty: 1,
    weight: "",
    size: "",
    comments: "",
    newImages: [],
    previewUrls: [],
  });
  const fileInputRef = useRef(null);

  // Swipe to Action State
  const [swipeProgress, setSwipeProgress] = useState(0);
  const [isSwiping, setIsSwiping] = useState(false);
  const trackRef = useRef(null);

  const session = useMemo(() => {
    const stored = localStorage.getItem("retailerSession");
    return stored ? JSON.parse(stored) : { id: "GUEST", partyName: "Retailer" };
  }, []);

  useEffect(() => {
    const fetchOrders = async () => {
      if (!session?.id) return;
      try {
        const q = query(
          collection(db, "partyOrders"),
          where("partyId", "==", session.id),
        );
        const snapshot = await getDocs(q);
        let orderData = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        orderData.sort(
          (a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0),
        );
        setOrders(orderData);
      } catch (error) {
        console.error("Error fetching orders:", error);
      } finally {
        setIsLoading(false);
      }
    };
    fetchOrders();
  }, [session]);

  useEffect(() => {
    let temp = [...orders];
    if (statusFilter !== "All")
      temp = temp.filter((o) => normalizeStatus(o.status) === statusFilter);
    if (search) {
      const s = search.toLowerCase();
      temp = temp.filter(
        (o) =>
          o.orderId?.toLowerCase().includes(s) ||
          o.product?.toLowerCase().includes(s) ||
          o.orderType?.toLowerCase().includes(s),
      );
    }
    setFilteredOrders(temp);
  }, [search, statusFilter, orders]);

  const openOrder = (order) => {
    setSelectedOrder(order);
    setDrawerMode("view");
    setActiveImageIndex(0); // Reset gallery to first image
    const details =
      order.orderType === "Single"
        ? order.singleItems?.[0]
        : order.castingDetails;
    setEditForm({
      qty: order.totalQuantity || details?.quantity || 1,
      weight: details?.weight || "",
      size: details?.length || details?.size || "",
      comments: order.comments || "",
    });
    document.body.style.overflow = "hidden";
  };

  const closeOrder = () => {
    setIsClosing(true);
    setTimeout(() => {
      setSelectedOrder(null);
      setIsClosing(false);
      setDrawerMode("view");
      setIsSuccess(false);
      setSwipeProgress(0);
      setActiveImageIndex(0);
      document.body.style.overflow = "auto";
    }, 300);
  };

  // --- UNIVERSAL SWIPE LOGIC ---
  const handleTouchStart = () => {
    if (!isSaving && !isSuccess) setIsSwiping(true);
  };
  const handleTouchMove = (e) => {
    if (!isSwiping || isSaving || isSuccess) return;
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const track = trackRef.current.getBoundingClientRect();
    const maxSwipe = track.width - 56;
    let newProgress = clientX - track.left - 28;
    if (newProgress < 0) newProgress = 0;
    if (newProgress > maxSwipe) newProgress = maxSwipe;
    setSwipeProgress(newProgress);

    if (newProgress >= maxSwipe * 0.95) {
      setIsSwiping(false);
      setSwipeProgress(maxSwipe);

      if (drawerMode === "edit") handleSaveEdit();
      else if (drawerMode === "variation") submitVariation();
    }
  };
  const handleTouchEnd = () => {
    if (isSwiping) {
      setIsSwiping(false);
      setSwipeProgress(0);
    }
  };

  // --- 1. EDIT EXISTING ORDER (Swipe to Save) ---
  const handleSaveEdit = async () => {
    setIsSaving(true);
    try {
      const docRef = doc(db, "partyOrders", selectedOrder.id);
      let updates = {
        totalQuantity: parseInt(editForm.qty) || 1,
        comments: editForm.comments,
        adminUnread: true,
        adminAlertMessage: `Retailer modified Order #${selectedOrder.orderId}`,
        editHistory: arrayUnion({
          editedBy: session.partyName,
          timestamp: serverTimestamp(),
          action: "Modified Specifications",
        }),
      };

      if (selectedOrder.orderType === "Single") {
        let updatedItems = [...selectedOrder.singleItems];
        updatedItems[0] = {
          ...updatedItems[0],
          weight: editForm.weight,
          length: editForm.size,
          quantity: parseInt(editForm.qty) || 1,
        };
        updates.singleItems = updatedItems;
      } else {
        updates.castingDetails = {
          ...selectedOrder.castingDetails,
          weight: editForm.weight,
          size: editForm.size,
        };
      }

      await updateDoc(docRef, updates);
      const updatedOrder = {
        ...selectedOrder,
        ...updates,
        editHistory: [
          ...(selectedOrder.editHistory || []),
          {
            editedBy: session.partyName,
            timestamp: { seconds: Date.now() / 1000 },
            action: "Modified Specifications",
          },
        ],
      };
      setOrders(
        orders.map((o) => (o.id === updatedOrder.id ? updatedOrder : o)),
      );
      setSelectedOrder(updatedOrder);

      setSuccessMessage("Modifications Saved!");
      setIsSuccess(true);
      setTimeout(() => {
        setIsSuccess(false);
        setDrawerMode("view");
        setSwipeProgress(0);
      }, 2000);
    } catch (error) {
      alert("Failed to update order.");
      setIsSaving(false);
      setSwipeProgress(0);
    }
  };

  // --- 2. VARIATION / REORDER ENGINE ---
  const startVariation = () => {
    setVariationForm({
      qty: 1,
      weight: "",
      size: "",
      comments: "Variation of Order #" + selectedOrder.orderId,
      newImages: [],
      previewUrls: [],
    });
    setDrawerMode("variation");
  };

  const handleExactReorder = () => {
    const details =
      selectedOrder.orderType === "Single"
        ? selectedOrder.singleItems?.[0]
        : selectedOrder.castingDetails;
    setVariationForm({
      qty: 1,
      weight: details?.weight || "",
      size: details?.length || details?.size || "",
      comments: "Exact Reorder of #" + selectedOrder.orderId,
      newImages: [],
      previewUrls: [],
    });
    setDrawerMode("variation");
  };

  const handleExtraImageUpload = (e) => {
    const files = Array.from(e.target.files);
    const urls = files.map((f) => URL.createObjectURL(f));
    setVariationForm((prev) => ({
      ...prev,
      newImages: [...prev.newImages, ...files],
      previewUrls: [...prev.previewUrls, ...urls],
    }));
  };

  const submitVariation = async () => {
    setIsSaving(true);
    try {
      let additionalImageUrls = [];
      if (variationForm.newImages.length > 0) {
        additionalImageUrls = await Promise.all(
          variationForm.newImages.map(async (img) => {
            const storageRef = ref(
              storage,
              `partyOrders/var_${Date.now()}_${img.name}`,
            );
            await uploadBytes(storageRef, img);
            return await getDownloadURL(storageRef);
          }),
        );
      }

      const baseImages = extractImages(selectedOrder);
      const finalImages = [...baseImages, ...additionalImageUrls];
      const orderId = await generateOrderId();
      const isCasting = selectedOrder.orderType === "Casting";

      let newDocData = {
        orderId,
        partyId: session.id,
        deliveryDate: "TBD",
        comments: variationForm.comments,
        status: "Pending",
        totalQuantity: parseInt(variationForm.qty) || 1,
        createdAt: serverTimestamp(),
        unseenCount: 1,
        orderType: selectedOrder.orderType,
        product: selectedOrder.product || selectedOrder.orderType,
        linkedToOrder: selectedOrder.orderId,
      };

      if (isCasting) {
        newDocData.files = finalImages;
        newDocData.castingDetails = {
          item: selectedOrder?.castingDetails?.item || "Custom",
          carat: selectedOrder?.castingDetails?.carat || "22K",
          weight: variationForm.weight,
          size: variationForm.size,
          stone: selectedOrder?.castingDetails?.stone || "N/A",
          budget: "",
        };
      } else {
        newDocData.singleItems = [
          {
            weight: variationForm.weight,
            length: variationForm.size,
            lengthUnit: "Standard",
            quantity: parseInt(variationForm.qty) || 1,
            itemStatus: "Pending",
            fileUrls: finalImages,
          },
        ];
      }

      const docRef = await addDoc(collection(db, "partyOrders"), newDocData);
      const freshOrder = {
        id: docRef.id,
        ...newDocData,
        createdAt: { seconds: Date.now() / 1000 },
      };
      setOrders((prev) => [freshOrder, ...prev]);

      setSuccessMessage("New Order Placed!");
      setIsSuccess(true);
      setTimeout(() => closeOrder(), 2500);
    } catch (error) {
      alert("Failed to submit order.");
      setIsSaving(false);
      setSwipeProgress(0);
    }
  };

  const getBadgeClass = (status) => {
    if (status === "Pending") return "warning";
    if (["Processing", "Polishing", "Setting"].includes(status))
      return "primary";
    if (["Complete", "Hallmarked", "Delivered"].includes(status))
      return "success";
    return "neutral";
  };

  return (
    <div className="v-rt-orders-app-shell fade-in">
      {/* Lightbox for Zooming Images */}
      {lightboxImg && (
        <div
          className="v-rt-orders-lightbox fade-in"
          onClick={() => setLightboxImg(null)}
        >
          <button className="lightbox-close">
            <FaTimes />
          </button>
          <img src={lightboxImg} alt="Detailed View" />
        </div>
      )}

      {/* 1. APP HEADER */}
      <div className="v-rt-orders-header">
        <div className="header-title-row">
          <h2>Order Desk</h2>
          <div className="v-rt-orders-search-box">
            <FaSearch className="icon" />
            <input
              type="text"
              placeholder="Search ID..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
        </div>

        <div className="kpi-scroll">
          <div
            className={`kpi-pill ${statusFilter === "All" ? "active" : ""}`}
            onClick={() => setStatusFilter("All")}
          >
            All Orders
          </div>
          <div
            className={`kpi-pill ${statusFilter === "Pending" ? "active" : ""}`}
            onClick={() => setStatusFilter("Pending")}
          >
            Pending Review
          </div>
          <div
            className={`kpi-pill ${statusFilter === "Processing" ? "active" : ""}`}
            onClick={() => setStatusFilter("Processing")}
          >
            In Production
          </div>
          <div
            className={`kpi-pill ${statusFilter === "Complete" ? "active" : ""}`}
            onClick={() => setStatusFilter("Complete")}
          >
            Ready
          </div>
          <div
            className={`kpi-pill ${statusFilter === "Delivered" ? "active" : ""}`}
            onClick={() => setStatusFilter("Delivered")}
          >
            Delivered
          </div>
        </div>
      </div>

      {/* 2. SCROLLABLE DATA LIST */}
      <div className="v-rt-orders-body">
        {isLoading ? (
          <div className="loader">
            <FaCircleNotch className="spin" /> Fetching Orders...
          </div>
        ) : filteredOrders.length === 0 ? (
          <div className="empty-state">
            <FaBoxOpen /> <p>No orders found.</p>
          </div>
        ) : (
          <div className="order-grid">
            {filteredOrders.map((order) => {
              const status = normalizeStatus(order.status);
              const allImgs = extractImages(order);
              const img = allImgs[0];
              const details =
                order.orderType === "Single"
                  ? order.singleItems?.[0]
                  : order.castingDetails;

              return (
                <div
                  key={order.id}
                  className="v-rt-luxury-card"
                  onClick={() => openOrder(order)}
                >
                  <div
                    className="card-image-wrapper"
                    style={{ position: "relative" }}
                  >
                    {img ? (
                      <>
                        <img src={img} alt="Product" loading="lazy" />
                        {/* Display +X badge if there are multiple images */}
                        {allImgs.length > 1 && (
                          <div
                            style={{
                              position: "absolute",
                              top: "8px",
                              left: "8px",
                              background: "rgba(0,0,0,0.65)",
                              color: "white",
                              padding: "2px 8px",
                              borderRadius: "12px",
                              fontSize: "0.75rem",
                              fontWeight: "bold",
                              backdropFilter: "blur(4px)",
                            }}
                          >
                            +{allImgs.length - 1}
                          </div>
                        )}
                      </>
                    ) : (
                      <FaGem className="no-img" />
                    )}
                    <span className={`status-badge ${getBadgeClass(status)}`}>
                      {status}
                    </span>
                  </div>
                  <div className="card-content">
                    <div className="card-top">
                      <h4>{order.product || order.orderType}</h4>
                      <span className="order-id">#{order.orderId}</span>
                    </div>
                    <div className="card-specs">
                      <span>{details?.weight || "TBD"}g</span>
                      <span className="dot">•</span>
                      <span>{details?.length || details?.size || "N/A"}</span>
                      <span className="dot">•</span>
                      <span>Qty: {order.totalQuantity || 1}</span>
                    </div>
                    <div className="card-date">
                      {formatDate(order.createdAt)}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* 3. THE SMART SLIDE-OVER DRAWER */}
      {selectedOrder && (
        <div className={`v-rt-orders-overlay ${isClosing ? "closing" : ""}`}>
          <div className="backdrop" onClick={closeOrder}></div>

          <div className={`drawer ${isClosing ? "closing" : ""}`}>
            {isSuccess ? (
              <div className="success-overlay pop-in">
                <FaCheckCircle className="check-icon" />
                <h3>{successMessage}</h3>
                <p>Synced with factory desk.</p>
              </div>
            ) : (
              <>
                <div className="drawer-header">
                  <div className="mobile-pull-bar"></div>
                  <div className="title-area">
                    <span className="id-badge">
                      Order #{selectedOrder.orderId}
                    </span>
                    <h2>{selectedOrder.product || selectedOrder.orderType}</h2>
                  </div>
                  <button className="close-btn" onClick={closeOrder}>
                    <FaTimes />
                  </button>
                </div>

                <div className="drawer-body">
                  {/* --- NEW: HERO + THUMBNAIL GALLERY IMPLEMENTATION --- */}
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: "10px",
                      marginBottom: "20px",
                    }}
                  >
                    {/* Hero Main Image (Controlled by activeImageIndex) */}
                    <div
                      className="hero-img-box"
                      onClick={() =>
                        setLightboxImg(
                          extractImages(selectedOrder)[activeImageIndex],
                        )
                      }
                      style={{ position: "relative" }}
                    >
                      {extractImages(selectedOrder)[activeImageIndex] ? (
                        <>
                          <img
                            src={extractImages(selectedOrder)[activeImageIndex]}
                            className="bg-blur"
                            alt="blur"
                          />
                          <img
                            src={extractImages(selectedOrder)[activeImageIndex]}
                            className="fg-img"
                            alt="product"
                          />
                          <div className="zoom-hint">
                            <FaExpandArrowsAlt /> Tap to Zoom
                          </div>
                        </>
                      ) : (
                        <div className="empty-img">
                          <FaBoxOpen /> No Image
                        </div>
                      )}
                    </div>

                    {/* Scrollable Thumbnail Strip (Overrides SCSS Conflicts) */}
                    {extractImages(selectedOrder).length > 1 && (
                      <div
                        style={{
                          display: "flex",
                          gap: "10px",
                          overflowX: "auto",
                          paddingBottom: "8px",
                          scrollbarWidth: "none", // Hides scrollbar in Firefox
                        }}
                      >
                        {extractImages(selectedOrder).map((img, idx) => (
                          <img
                            key={idx}
                            src={img}
                            alt={`Thumbnail ${idx + 1}`}
                            onClick={() => setActiveImageIndex(idx)}
                            style={{
                              width: "65px",
                              height: "65px",
                              objectFit: "cover",
                              borderRadius: "8px",
                              flexShrink: 0,
                              cursor: "pointer",
                              border:
                                activeImageIndex === idx
                                  ? "2px solid #3b82f6"
                                  : "1px solid #cbd5e1",
                              opacity: activeImageIndex === idx ? 1 : 0.6,
                              transition: "all 0.2s ease",
                              boxShadow:
                                activeImageIndex === idx
                                  ? "0 2px 8px rgba(59, 130, 246, 0.3)"
                                  : "none",
                            }}
                          />
                        ))}
                      </div>
                    )}
                  </div>
                  {/* --- END GALLERY --- */}

                  {/* VIEW MODE */}
                  {drawerMode === "view" && (
                    <>
                      <div className="section-block timeline-block">
                        <div className="timeline">
                          <div className="track">
                            <div
                              className="fill"
                              style={{
                                width: `${(getProgressStep(normalizeStatus(selectedOrder.status)) - 1) * 33.33}%`,
                              }}
                            ></div>
                          </div>
                          <div className="steps">
                            {[
                              "Pending",
                              "In Factory",
                              "Finishing",
                              "Ready",
                            ].map((step, i) => (
                              <div
                                key={step}
                                className={`step ${getProgressStep(normalizeStatus(selectedOrder.status)) >= i + 1 ? "active" : ""}`}
                              >
                                <span>{step}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>

                      <div className="section-block specs-block">
                        <div className="section-title-row">
                          <h3>Specifications</h3>
                          {normalizeStatus(selectedOrder.status) ===
                          "Pending" ? (
                            <button
                              className="inline-edit-btn"
                              onClick={() => setDrawerMode("edit")}
                            >
                              <FaEdit /> Edit Spec
                            </button>
                          ) : (
                            <span className="locked-note">Locked</span>
                          )}
                        </div>
                        <div className="specs-grid">
                          <div className="spec-item">
                            <label>Weight</label>
                            <span>
                              {selectedOrder.orderType === "Single"
                                ? selectedOrder.singleItems?.[0]?.weight
                                : selectedOrder.castingDetails?.weight || "TBD"}
                              g
                            </span>
                          </div>
                          <div className="spec-item">
                            <label>Size</label>
                            <span>
                              {selectedOrder.orderType === "Single"
                                ? selectedOrder.singleItems?.[0]?.length
                                : selectedOrder.castingDetails?.size || "TBD"}
                            </span>
                          </div>
                          <div className="spec-item">
                            <label>Quantity</label>
                            <span>{selectedOrder.totalQuantity || 1} pcs</span>
                          </div>
                        </div>
                        {selectedOrder.comments && (
                          <div className="instructions-box">
                            <label>Instructions</label>
                            <p>{selectedOrder.comments}</p>
                          </div>
                        )}
                      </div>

                      {/* Power Feature: Exact Reorder or Custom Variation */}
                      <div className="variation-cta-block">
                        <div className="content">
                          <h4>Love this design?</h4>
                          <p>
                            Order it again exactly as is, or customize weight,
                            size, and attach pendants.
                          </p>
                        </div>
                        <div className="btn-row">
                          <button
                            className="btn-variation exact"
                            onClick={handleExactReorder}
                          >
                            <FaRedoAlt /> Exact Reorder
                          </button>
                          <button
                            className="btn-variation"
                            onClick={startVariation}
                          >
                            <FaCopy /> Custom Variation
                          </button>
                        </div>
                      </div>

                      {selectedOrder.editHistory &&
                        selectedOrder.editHistory.length > 0 && (
                          <div className="section-block history-block">
                            <h3>
                              <FaHistory /> Modification Log
                            </h3>
                            <div className="history-list">
                              {selectedOrder.editHistory.map((log, idx) => (
                                <div key={idx} className="history-item">
                                  <div className="dot"></div>
                                  <div className="info">
                                    <span className="action">{log.action}</span>
                                    <span className="meta">
                                      By {log.editedBy} •{" "}
                                      {formatDate(log.timestamp)}
                                    </span>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                    </>
                  )}

                  {/* EDIT MODE */}
                  {drawerMode === "edit" && (
                    <div className="edit-form-block pop-in">
                      <h3>Edit Current Order</h3>
                      <p className="helper">
                        Modifications will instantly update your pending ticket.
                      </p>

                      <div className="input-row">
                        <div className="input-group">
                          <label>Weight (g)</label>
                          <input
                            type="number"
                            value={editForm.weight}
                            onChange={(e) =>
                              setEditForm({
                                ...editForm,
                                weight: e.target.value,
                              })
                            }
                          />
                        </div>
                        <div className="input-group">
                          <label>Size / Length</label>
                          <input
                            type="text"
                            value={editForm.size}
                            onChange={(e) =>
                              setEditForm({ ...editForm, size: e.target.value })
                            }
                          />
                        </div>
                      </div>
                      <div className="input-group">
                        <label>Total Quantity</label>
                        <input
                          type="number"
                          value={editForm.qty}
                          onChange={(e) =>
                            setEditForm({ ...editForm, qty: e.target.value })
                          }
                        />
                      </div>
                      <div className="input-group">
                        <label>Instructions</label>
                        <textarea
                          value={editForm.comments}
                          onChange={(e) =>
                            setEditForm({
                              ...editForm,
                              comments: e.target.value,
                            })
                          }
                          rows="3"
                        />
                      </div>
                    </div>
                  )}

                  {/* VARIATION MODE */}
                  {drawerMode === "variation" && (
                    <div className="variation-form-block pop-in">
                      <h3>Configure New Order</h3>
                      <p className="helper">
                        Creates a <strong>fresh order ticket</strong> linked to
                        this exact image.
                      </p>

                      <div className="input-row mt-16">
                        <div className="input-group">
                          <label>New Weight (g)</label>
                          <input
                            type="number"
                            placeholder="e.g. 25"
                            value={variationForm.weight}
                            onChange={(e) =>
                              setVariationForm({
                                ...variationForm,
                                weight: e.target.value,
                              })
                            }
                          />
                        </div>
                        <div className="input-group">
                          <label>New Size</label>
                          <input
                            type="text"
                            placeholder="e.g. 30 inch"
                            value={variationForm.size}
                            onChange={(e) =>
                              setVariationForm({
                                ...variationForm,
                                size: e.target.value,
                              })
                            }
                          />
                        </div>
                      </div>
                      <div className="input-group">
                        <label>Total Quantity</label>
                        <input
                          type="number"
                          min="1"
                          value={variationForm.qty}
                          onChange={(e) =>
                            setVariationForm({
                              ...variationForm,
                              qty: e.target.value,
                            })
                          }
                        />
                      </div>
                      <div className="input-group">
                        <label>Specific Instructions</label>
                        <textarea
                          placeholder="e.g. Add the attached pendant..."
                          value={variationForm.comments}
                          onChange={(e) =>
                            setVariationForm({
                              ...variationForm,
                              comments: e.target.value,
                            })
                          }
                          rows="2"
                        />
                      </div>

                      <div className="extra-media-box">
                        <label>Attach Component (e.g., Custom Pendant)</label>
                        <div
                          className="upload-zone"
                          onClick={() => fileInputRef.current.click()}
                        >
                          <FaCamera className="cam-icon" />{" "}
                          <span>Add Photo</span>
                        </div>
                        <input
                          type="file"
                          multiple
                          accept="image/*"
                          ref={fileInputRef}
                          style={{ display: "none" }}
                          onChange={handleExtraImageUpload}
                        />
                        {variationForm.previewUrls.length > 0 && (
                          <div className="preview-row">
                            {variationForm.previewUrls.map((url, i) => (
                              <img
                                key={i}
                                src={url}
                                alt="extra"
                                className="mini-thumb pop-in"
                              />
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Spacer prevents content from hiding behind the lifted bottom bar */}
                  <div style={{ height: "60px" }}></div>
                </div>

                {/* --- GLOBAL APP OVERLAP FIX: THE LIFTED BOTTOM BAR --- */}
                <div className="drawer-footer">
                  {drawerMode === "view" ? (
                    <div className="action-row">
                      <button
                        className="btn support"
                        onClick={() =>
                          window.open(
                            `https://wa.me/?text=Hi, regarding my Order #${selectedOrder.orderId}...`,
                            "_blank",
                          )
                        }
                      >
                        <FaWhatsapp /> Support
                      </button>
                      <button className="btn primary">
                        <FaMapMarkerAlt /> Track Status
                      </button>
                    </div>
                  ) : (
                    <div className="action-stack">
                      <button
                        className="text-btn cancel"
                        onClick={() => setDrawerMode("view")}
                        disabled={isSaving}
                      >
                        Cancel Action
                      </button>

                      <div
                        className="swipe-track"
                        ref={trackRef}
                        onTouchMove={handleTouchMove}
                        onTouchEnd={handleTouchEnd}
                        onMouseMove={handleTouchMove}
                        onMouseUp={handleTouchEnd}
                        onMouseLeave={handleTouchEnd}
                      >
                        {isSaving ? (
                          <div className="submitting-state">
                            <FaCircleNotch className="spin" /> Processing...
                          </div>
                        ) : (
                          <>
                            <div
                              className="swipe-fill"
                              style={{ width: `${swipeProgress + 28}px` }}
                            ></div>
                            <span className="swipe-text">
                              {drawerMode === "edit"
                                ? "Swipe to Save Edits"
                                : "Swipe to Place Order"}
                            </span>
                            <div
                              className="swipe-thumb"
                              style={{
                                transform: `translateX(${swipeProgress}px)`,
                              }}
                              onTouchStart={handleTouchStart}
                              onMouseDown={handleTouchStart}
                            >
                              <FaArrowRight className="thumb-icon" />
                            </div>
                          </>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default RetailerOrders;
